import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  defaultGetNextPageParam,
  sortObjectKeys,
} from '@vindr/utils/helpers/common.helper';
import {
  createAssign,
  deleteAssign,
  getAssigns,
  getDetailAssign,
  ParamsCreateAssign,
  ParamsDeleteAssign,
  ParamsUpdateAssign,
  updateAssign,
} from '../apis/assign.api';
import { queryKey } from '../constants/queryClientKey';

export function useGetAssigns(params) {
  return useQuery(
    [queryKey.assigns, sortObjectKeys(params)],
    () => getAssigns(params),
    {}
  );
}

export function useGetDetailAssign(id, config) {
  return useQuery(['getDetailAssign', id], () => getDetailAssign(id), {
    ...config,
    onSuccess: (data) => {
      config?.onSuccess?.(data.data);
    },
  }) as UseQueryResult<Awaited<ReturnType<typeof getDetailAssign>>>;
}

export const useGetAssignsInterval = (params) => {
  return useQuery(
    [queryKey.assigns_interval, sortObjectKeys(params)],
    () => getAssigns(params),
    {
      refetchInterval: 10000,
    }
  );
};

export const useInfinityAssigned = (
  params: Parameters<typeof getAssigns>[0],
  options?: UseInfiniteQueryOptions
) => {
  const queryInfo = useInfiniteQuery(
    [queryKey.assigns, sortObjectKeys(params)],
    (e) =>
      getAssigns({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: defaultGetNextPageParam,
      keepPreviousData: true,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getAssigns>>>;

  const data = queryInfo.data?.pages.map((page) => page.data || []).flat();

  return {
    ...queryInfo,
    data,
    count: queryInfo.data?.pages[0].count,
  };
};

export function useCreateAssign() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateAssign) => createAssign(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },
      onError: (err) => {
        console.error('useCreateOrderGroup:', err);
      },
    }
  );
}

export function useUpdateAssign() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsUpdateAssign) => updateAssign(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },
      onError: (err) => {
        console.error('useUpdateAssign:', err);
      },
    }
  );
}

export function useDeleteAssign() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsDeleteAssign) => deleteAssign(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },
      onError: (err) => {
        console.error('useDeleteOrder:', err);
      },
    }
  );
}
